import { useState, useEffect, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Button from "../components/ui/Button";
import Alert from "../components/ui/Alert";
import { Input, PasswordInput } from "../components/ui/Input";
import AppBanner from "../components/AppBanner";
import { apiService } from "../utils/apiService";
import { useLoadingState } from "../hooks/useLoadingState";
import {
  validatePassword,
  validatePasswordConfirmation,
} from "../utils/validationUtils";

const ResetPassword = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [tokenValid, setTokenValid] = useState(false);
  const { loading, withLoading } = useLoadingState();

  const validateToken = useCallback(async () => {
    try {
      await withLoading(async () => {
        await apiService.get(`/password-reset/validate/${token}`);
        setTokenValid(true);
      });
    } catch (err) {
      setError("Invalid or expired token");
      setTokenValid(false);
    }
  }, [token, withLoading]);

  useEffect(() => {
    if (token) {
      validateToken();
    }
  }, [token, validateToken]);

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    try {
      await withLoading(async () => {
        await apiService.post("/password-reset/request", { email });
      });

      setSuccess("Password reset link sent to your email");
      setEmail("");
    } catch (err) {
      apiService.handleError(err, setError, "Failed to request password reset");
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError("");

    // Validate password confirmation
    const confirmationValidation = validatePasswordConfirmation(
      password,
      confirmPassword,
    );
    if (!confirmationValidation.isValid) {
      setError(confirmationValidation.error);
      return;
    }

    // Validate password strength
    const passwordValidation = validatePassword(password);
    if (!passwordValidation.isValid) {
      setError(passwordValidation.errors[0]);
      return;
    }

    try {
      await withLoading(async () => {
        await apiService.post("/password-reset/reset", {
          token,
          newPassword: password,
        });
      });

      setSuccess("Password reset successful");
      setTimeout(() => navigate("/login"), 2000);
    } catch (err) {
      apiService.handleError(err, setError, "Failed to reset password");
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <AppBanner />
      <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <h1 className="text-center text-3xl font-extrabold text-gray-900">
              {token ? "Reset Your Password" : "Forgot Password"}
            </h1>
            <p className="text-center text-sm text-gray-600 mt-2">
              {token
                ? "Enter your new password"
                : "We'll send you a reset link"}
            </p>
          </div>

          <div className="bg-white shadow-md rounded-lg p-8">
            {error && (
              <Alert color="red" className="mb-4">
                {error}
              </Alert>
            )}
            {success && (
              <Alert color="green" className="mb-4">
                {success}
              </Alert>
            )}

            {!token ? (
              <form onSubmit={handleRequestReset} className="space-y-6">
                <Input
                  label="Email"
                  type="email"
                  placeholder="you@example.com"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <Button fullWidth type="submit" loading={loading}>
                  Send Reset Link
                </Button>
              </form>
            ) : (
              tokenValid && (
                <form onSubmit={handleResetPassword} className="space-y-6">
                  <PasswordInput
                    label="New Password"
                    placeholder="Your new password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <div className="text-sm text-gray-600">
                    Password must be at least 6 characters and contain:
                    <ul className="list-disc list-inside mt-1 space-y-1">
                      <li>At least one uppercase letter</li>
                      <li>At least one lowercase letter</li>
                      <li>At least one number</li>
                    </ul>
                  </div>
                  <PasswordInput
                    label="Confirm Password"
                    placeholder="Confirm your password"
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                  <Button fullWidth type="submit" loading={loading}>
                    Reset Password
                  </Button>
                </form>
              )
            )}

            <div className="text-center mt-6">
              <Link
                to="/login"
                className="font-medium text-blue-600 hover:text-blue-500"
              >
                Back to login
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
